import React from "react";
import { DropdownInput, IOption } from "component-library";

import { ControlSection } from "../../../../../Common";
import { IHeightViewModel } from "../../../../models/views";
import { MeasurementSystems } from "../../../../models";

export enum LengthUnits {
  inch = "inch",
  cm = "cm",
  foot = "foot",
}

export interface IHeightContent {
  heightSectionName: string;
  feetField: string;
  inchField: string;
  centimetersField: string;
  feetPlaceholder: string;
  inchPlaceholder: string;
  centimetersPlaceholder: string;
  imperialSystemName: string;
  metricSystemName: string;
}

interface IHeightProps {
  model: IHeightViewModel;
  content: IHeightContent;
  handleChanges: (model: IHeightViewModel) => void;
}

interface IHeightState {
  measurementSystem: MeasurementSystems;
}

export default class Height extends React.Component<
  IHeightProps,
  IHeightState
> {
  constructor(props: IHeightProps) {
    super(props);

    this.state = {
      measurementSystem: MeasurementSystems.imperial,
    };
  }

  public render() {
    const { measurementSystem } = this.state;
    const { content } = this.props;

    const isImperial = measurementSystem === MeasurementSystems.imperial;

    return (
      <ControlSection
        name={content.heightSectionName}
        button={{
          name: isImperial
            ? content.metricSystemName
            : content.imperialSystemName,
          clickHandler: this.changeMeasurementSystem,
        }}
      >
        <div className="c-basic-info-form__height">
          {isImperial && (
            <div className="c-basic-info-form__height-foot">
              {this.renderDropDown(LengthUnits.foot, {
                label: content.feetField,
                placeholder: content.feetPlaceholder,
              })}
            </div>
          )}
          <div className="c-basic-info-form__height-inch">
            {isImperial
              ? this.renderDropDown(LengthUnits.inch, {
                  label: content.inchField,
                  placeholder: content.inchPlaceholder,
                })
              : this.renderDropDown(LengthUnits.cm, {
                  label: content.centimetersField,
                  placeholder: content.centimetersPlaceholder,
                })}
          </div>
        </div>
      </ControlSection>
    );
  }

  private renderDropDown = (
    unit: LengthUnits,
    text: { label: string; placeholder: string }
  ) => {
    let options: IOption<number>[] = [
      this.getEmptyOption(text.placeholder),
      ...this.getOptions(unit),
    ];

    const height = this.props.model.height;
    const { feet = 0, inches = 0 } = this.centimetersToImperial(height);

    let value: number | undefined = undefined;

    if (height) {
      switch (unit) {
        case LengthUnits.foot:
          value = Math.round(feet);
          break;
        case LengthUnits.inch:
          value = Math.round(inches);
          break;
        case LengthUnits.cm:
          value = Math.round(height);
          break;
      }
    }

    return (
      <DropdownInput<number>
        key={unit + this.state.measurementSystem.toString()}
        id={`height${unit}`}
        variant="select"
        options={options}
        name={unit}
        value={value}
        {...text}
        valueChanged={this.dropdownChanged(unit)}
      />
    );
  };

  private dropdownChanged = (unit: LengthUnits) => {
    return (value: number) => {
      const updatedModel = { ...this.props.model };
      if (unit !== LengthUnits.cm) {
        let { feet, inches } = this.centimetersToImperial(updatedModel.height);

        if (unit === LengthUnits.foot) {
          feet = value || 0;
        } else {
          inches = value || 0;
        }

        updatedModel.height = Math.round((feet * 12 + inches) * 25.4) / 10;
      } else {
        updatedModel.height = value;
      }

      if (
        updatedModel.height !== this.props.model.height ||
        Number.isNaN(value)
      ) {
        this.props.handleChanges(updatedModel);
      }
    };
  };

  private changeMeasurementSystem = () =>
    this.setState((state) => ({
      measurementSystem:
        state.measurementSystem === MeasurementSystems.imperial
          ? MeasurementSystems.metric
          : MeasurementSystems.imperial,
    }));

  private getOptions = (unit: LengthUnits): IOption<number>[] => {
    let options: IOption<number>[] = [];

    switch (unit) {
      case LengthUnits.foot:
        options = this.getNumberList(2, 7);
        break;
      case LengthUnits.inch:
        options = this.getNumberList(0, 11);
        break;
      case LengthUnits.cm:
        options = this.getNumberList(61, 241);
        break;
    }

    return options;
  };

  private getEmptyOption = (placeholder: string): IOption<number> => ({
    value: NaN,
    name: placeholder,
  });

  private getNumberList = (from: number, to: number) => {
    const options: IOption<number>[] = [];
    for (let height = from; height <= to; height++) {
      options.push({ name: height.toString(), value: height });
    }

    return options;
  };

  private centimetersToImperial = (centimeters?: number) => {
    let feet = 0;
    let inches = 0;

    if (centimeters) {
      let realInches = Math.round(centimeters * 0.3937);
      inches = realInches % 12;
      feet = (realInches - inches) / 12;
    }
    return { inches, feet };
  };
}
